import { lazy } from "react"

const CHUNK_RELOAD_KEY = "soho_chunk_reload_attempt_v1"

function isChunkLoadError(error) {
  const message = String(error?.message ?? error ?? "")
  return error?.name === "ChunkLoadError" || /Loading chunk [\w-]+ failed/i.test(message)
}

function importWithRetry(importer, retriesLeft = 1) {
  return importer().catch((error) => {
    if (!isChunkLoadError(error) || retriesLeft <= 0) {
      throw error
    }

    const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY)
    if (!hasReloaded) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "1")
      window.location.reload()
      return new Promise(() => {})
    }

    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
    return new Promise((resolve, reject) => {
      window.setTimeout(() => {
        importWithRetry(importer, retriesLeft - 1).then(resolve).catch(reject)
      }, 800)
    })
  })
}

function createPageLoader(importer) {
  return () => importWithRetry(importer)
}

const importDashboard = () =>
  import(/* webpackChunkName: "page-dashboard" */ "../components/dashboard/Dashboard")
const importDashboard2 = () =>
  import(/* webpackChunkName: "page-dashboard-2" */ "../components/dashboard/Dashboard2")
const importCalendar = () =>
  import(/* webpackChunkName: "page-calendar" */ "../components/calendar/calendar-page/CalendarPage")
const importInbox = () =>
  import(/* webpackChunkName: "page-inbox" */ "../components/ai-community-inbox/AICommunityInbox.jsx")
const importPlanner = () =>
  import(/* webpackChunkName: "page-planner" */ "../components/ai-planner/AIPlanner")
const importSettings = () =>
  import(/* webpackChunkName: "page-settings" */ "../components/settings/setting/SettingsPage")
const importSocialNetwork = () =>
  import(/* webpackChunkName: "page-social-network" */ "../components/settings/social-network/socialNetwork")
const importMediaLibrary = () =>
  import(/* webpackChunkName: "page-media-library" */ "../components/MediaLibrary/MediaLibrary")
const importAssetManager = () =>
  import(/* webpackChunkName: "page-asset-manager" */ "../components/AIAssetManager/AIAssetManager")
const importAssetManagerDetails = () =>
  import(/* webpackChunkName: "page-asset-details" */ "../components/AIAssetManager/AIAssetManagerDetails")
const importAssetManagerCategory = () =>
  import(/* webpackChunkName: "page-asset-category" */ "../components/AIAssetManager/AIAssetManagerCategoryPage")
const importSettingUp = () =>
  import(/* webpackChunkName: "page-setting-up" */ "../components/settingup/SettingUp")
const importOrganizationSettings = () =>
  import(/* webpackChunkName: "page-org-settings" */ "../components/organization-settings/OrganizationSettings")
const importBilling = () =>
  import(/* webpackChunkName: "page-billing" */ "../components/billing-invoices/BillingInvoices")
const importAudits = () =>
  import(/* webpackChunkName: "page-audits" */ "../components/ai-audit/AIAuditsPage.jsx")
const importUgc = () =>
  import(/* webpackChunkName: "page-ugc" */ "../pages/UGC")

const loadDashboard = createPageLoader(importDashboard)
const loadDashboard2 = createPageLoader(importDashboard2)
const loadCalendar = createPageLoader(importCalendar)
const loadInbox = createPageLoader(importInbox)
const loadPlanner = createPageLoader(importPlanner)
const loadSettings = createPageLoader(importSettings)
const loadSocialNetwork = createPageLoader(importSocialNetwork)
const loadMediaLibrary = createPageLoader(importMediaLibrary)
const loadAssetManager = createPageLoader(importAssetManager)
const loadAssetManagerDetails = createPageLoader(importAssetManagerDetails)
const loadAssetManagerCategory = createPageLoader(importAssetManagerCategory)
const loadSettingUp = createPageLoader(importSettingUp)
const loadOrganizationSettings = createPageLoader(importOrganizationSettings)
const loadBilling = createPageLoader(importBilling)
const loadAudits = createPageLoader(importAudits)
const loadUgc = createPageLoader(importUgc)

export const Dashboard = lazy(loadDashboard)
export const Dashboard2 = lazy(loadDashboard2)
export const Calendar = lazy(loadCalendar)
export const AICommunityInbox = lazy(loadInbox)
export const AIPlanner = lazy(loadPlanner)
export const SettingsPage = lazy(() => loadSettings().then((m) => ({ default: m.SettingsPage })))
export const SocialNetwork = lazy(loadSocialNetwork)
export const MediaLibrary = lazy(loadMediaLibrary)
export const AIAssetManager = lazy(loadAssetManager)
export const AIAssetManagerDetails = lazy(loadAssetManagerDetails)
export const AIAssetManagerCategoryPage = lazy(loadAssetManagerCategory)
export const SettingUp = lazy(loadSettingUp)
export const OrganizationSettings = lazy(loadOrganizationSettings)
export const BillingInvoices = lazy(loadBilling)
export const AIAuditsPage = lazy(loadAudits)
export const UGC = lazy(loadUgc)

const PREFETCH_BY_PATH = {
  "/dashboard": importDashboard,
  "/multi-property-dashboard": importDashboard2,
  "/calendar": importCalendar,
  "/ai-community-inbox": importInbox,
  "/ai-planner": importPlanner,
  "/settings": importSettings,
  "/settings/social-network": importSocialNetwork,
  "/medialibrary": importMediaLibrary,
  "/aiassetmanager": importAssetManager,
  "/ai-asset-manager-details": importAssetManagerDetails,
  "/ai-audits": importAudits,
  "/ugc": importUgc,
  "/organization-settings": importOrganizationSettings,
  "/organization-settings/billing": importBilling,
}

if (typeof window !== "undefined") {
  importDashboard().catch(() => {})
  importDashboard2().catch(() => {})
}

function runPrefetch(loader) {
  if (!loader) return
  loader().catch(() => {})
}

export function prefetchRoute(path) {
  if (!path) return
  const loader = PREFETCH_BY_PATH[path]
  if (loader) {
    runPrefetch(loader)
    return
  }
  const match = Object.keys(PREFETCH_BY_PATH).find(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  )
  if (match) runPrefetch(PREFETCH_BY_PATH[match])
}
